// fonts.js — load the partner BRAND fonts into the embed via @font-face (D14 plan §9).
//
// The print frame (title / ship name / journey details) is drawn CLIENT-SIDE as SVG in
// preview.js, so its glyphs are OURS to render — the brand fonts must be available in the
// browser for full fidelity. (The headless worker only rasterises the map inset + port
// labels, whose glyphs come from MapTiler's own style — so brand fonts are NOT needed
// there in this client-frame architecture.)
//
// Family names match what AppFactory.convertFileNameToFontFamily produces from the asset
// filenames, and what the style configs reference in image*Font keys. The transform is
// lossy (extension dropped, every word force-capitalised), so the filename can NOT be derived
// from the family — each pair below was matched against the real `fonts/` bucket listing.
//
// KNOWN LIMITATION: this list is static, so a style naming a family that isn't here falls
// back to system-ui SILENTLY (fontFamilyWithFallback in animMath.js) — no event, no error,
// and measureTextWidth then measures the fallback so title wrapping shifts too. Adding a
// partnership brand currently needs a widget release, not just a bucket upload. The fix is
// to have resolve_frame emit a fontAssets map on the frame recipe and build these rules
// from it; see working-directory/preview-widget-brand-fonts-plan.md §6.
//
// Licensing: Praxis/Termina confirmed OK by the owner for the gated prototype. The
// partnership faces below (Museo Sans, Neutra2Text, Austin, Atlas, MSC Pangea, Adieu,
// Quicksand) are commercial faces added for the 11-style render test — NOT cleared for
// public storefront use. Confirm before any production release.

const FONTS = [
  { family: "Termina Demi", file: "termina_demi.otf", format: "opentype" },
  { family: "Inter Semibold", file: "inter_semibold.ttf", format: "truetype" },
  { family: "Inter Regular", file: "inter_regular.ttf", format: "truetype" },
  { family: "PraxisLT-Bold", file: "PraxisLT-Bold.ttf", format: "truetype" },
  { family: "PraxisLT-Regular", file: "PraxisLT-Regular.ttf", format: "truetype" },
  // --- Partnership brand faces (11-style test set, 2026-09) ----------------
  { family: "Adieu Regular", file: "Adieu_Regular.otf", format: "opentype" },
  { family: "Museo Sans", file: "Museo_Sans.otf", format: "opentype" },
  { family: "Austin Roman", file: "austin_roman.otf", format: "opentype" },
  { family: "Atlas Regular", file: "atlas_regular.otf", format: "opentype" },
  { family: "Neutra2Text-Bold", file: "Neutra2Text-Bold.otf", format: "opentype" },
  { family: "Neutra2Text-Light", file: "Neutra2Text-Light.otf", format: "opentype" },
  { family: "Quicksand-Bold", file: "Quicksand-Bold.ttf", format: "truetype" },
  { family: "Quicksand-Regular", file: "Quicksand-Regular.ttf", format: "truetype" },
  { family: "MSC Pangea Bold", file: "MSC_Pangea_Bold.otf", format: "opentype" },
  { family: "MSC Pangea Regular", file: "MSC_Pangea_Regular.otf", format: "opentype" },
];

// Relative to apiBaseUrl, which is now the mapper-api `/api/v3` base (e.g.
// `https://api.example.com/api/v3`). Resolved URL: `<apiBaseUrl>/style/assets/<file>`.
const ASSET_PATH = "/style/assets";

// Injected once per (document, apiBaseUrl). Stored on the document so multiple embed
// instances on one page don't duplicate the <style>.
const INJECTED = new WeakMap(); // document -> Set(apiBaseUrl)

/**
 * ensureBrandFonts(apiBaseUrl) -> Promise that resolves when the brand fonts have
 * loaded (or settled). Injects @font-face rules pointing at the API asset route the
 * first time, kicks off loading each face, and resolves on document.fonts.ready.
 * Never rejects — a missing font just falls back to the system stack (composeFrame
 * already wraps families with a fallback).
 */
export function ensureBrandFonts(apiBaseUrl = "") {
  if (typeof document === "undefined") return Promise.resolve();
  const base = apiBaseUrl || "";
  let seen = INJECTED.get(document);
  if (!seen) { seen = new Set(); INJECTED.set(document, seen); }

  if (!seen.has(base)) {
    seen.add(base);
    const css = FONTS.map(
      (f) =>
        `@font-face{font-family:'${f.family}';` +
        `src:url('${base}${ASSET_PATH}/${encodeURIComponent(f.file)}') format('${f.format}');` +
        `font-display:swap;}`
    ).join("\n");
    const style = document.createElement("style");
    style.setAttribute("data-vp-brand-fonts", base || "same-origin");
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Trigger actual loading (font-face rules are lazy until used). Swallow per-font
  // failures so one missing asset can't block the others.
  const loads = FONTS.map((f) => {
    try {
      return document.fonts.load(`16px '${f.family}'`).catch(() => {});
    } catch {
      return Promise.resolve();
    }
  });
  return Promise.all(loads).then(() => document.fonts.ready).catch(() => {});
}
